import { useNavigate } from "react-router-dom";
import { ChatBubbleLeftIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import cn from "classnames";
import { revealProps } from "../common/Reveal";
import { relativeTime } from "../common/relativeTime";
import { byline, CATEGORY_LABELS, FeatureRequestItem } from "./api";
import StatusChip from "./StatusChip";
import VoteButton from "./VoteButton";

interface Props {
  item: FeatureRequestItem;
  index?: number;
  onVote: (item: FeatureRequestItem) => void;
  voting?: boolean;
  // Roadmap columns: no excerpt, smaller vote pill.
  compact?: boolean;
}

// Board row: vote pill on the left, content on the right. Border/hover
// treatment matches common/ItemCard so the board reads like the other
// list pages; the icon tile is replaced by the vote button.
const RequestCard: React.FC<Props> = ({ item, index = 0, onVote, voting, compact }) => {
  const navigate = useNavigate();
  const hidden = Boolean(item.hidden_at);
  return (
    <div
      {...revealProps(index)}
      onClick={() => navigate(`/feature-requests/${item.id}`)}
      className={cn(
        "reveal p-4 border rounded-lg bg-base-100 border-base-300 transition-all group cursor-pointer hover:border-blue-300 dark:hover:border-blue-800 hover:shadow-md",
        hidden && "opacity-60 border-dashed"
      )}
    >
      <div className="flex items-start gap-3">
        <VoteButton count={item.vote_count} voted={item.hasVoted} onToggle={() => onVote(item)} busy={voting} size={compact ? "sm" : "md"} />
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-sm line-clamp-2 min-w-0 group-hover:text-blue-600 dark:group-hover:text-blue-300 transition-colors">{item.title}</h3>
          {!compact && <p className="text-xs text-base-content/60 mt-0.5 line-clamp-2 whitespace-pre-line">{item.body}</p>}
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <StatusChip status={item.status} />
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-base-200 text-base-content/60">{CATEGORY_LABELS[item.category]}</span>
            {hidden && (
              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-base-200 text-base-content/60">
                <EyeSlashIcon className="size-3" /> Hidden
              </span>
            )}
            <span className="text-xs text-base-content/40 truncate">
              {byline(item)} · {relativeTime(item.created_at)}
            </span>
            {item.comment_count > 0 && (
              <span className="inline-flex items-center gap-0.5 text-xs text-base-content/40 ml-auto">
                <ChatBubbleLeftIcon className="size-3.5" /> {item.comment_count}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestCard;
